// ✅ P0-4 + P1-4 FIX: Centralized language change logic
// Prevents infinite loops and ensures URL/language sync

import { useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { getLogger } from '@/lib/logger/logger';

import { updateUrlLanguage } from '../../i18n';
import { changeLanguageCore } from '../utils/changeLanguage';

import { useRegion } from './useRegion';

const logger = getLogger('useLanguage');

/**
 * Centralized hook for language management
 * Handles language changes, URL updates, and cache invalidation
 */
export const useLanguage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { isOverseas } = useRegion();

  /**
   * Change language with all side effects handled
   * - Clears features cache (via changeLanguageCore)
   * - Updates i18n language (via changeLanguageCore)
   * - Updates HTML lang attribute (via changeLanguageCore)
   * - Saves to localStorage (via changeLanguageCore)
   * - Updates URL (domestic users only, hook-specific)
   */
  const changeLanguage = useCallback(
    async (newLang: 'ko' | 'en' | 'ja') => {
      const prevLang = i18n.language;

      // Core: cache clear + i18n + HTML lang + localStorage
      await changeLanguageCore(newLang);

      // Hook-specific: URL update (all users — overseas uses /ja prefix, domestic uses /en prefix)
      if (prevLang !== newLang) {
        const newPath = updateUrlLanguage(newLang, isOverseas);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        navigate(newPath, { replace: true });
      }

      logger.info('[useLanguage] Language change complete:', newLang);
    },
    [i18n, navigate, isOverseas],
  );

  return {
    currentLanguage: i18n.language as 'ko' | 'en' | 'ja',
    changeLanguage,
    isOverseas,
  };
};
